import { Pause, Play, RotateCcw, Maximize2, Presentation, Timer } from "lucide-react";
import { openPresenter } from "./sync";
import { TOTAL_SECONDS, SLIDE_META, SLIDE_STARTS } from "./config";
import { DashMark } from "./ui";

const fmt = (s) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

const IconBtn = ({ onClick, label, testId, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testId}
    className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-zinc-200 transition-colors duration-300 hover:border-[var(--dash)] hover:bg-[var(--dash)] hover:text-black"
  >
    {children}
  </button>
);

export const TopBar = ({ timer, index, rehearse, onRehearse }) => {
  const { elapsed, running, toggle, reset } = timer;
  const pct = (elapsed / TOTAL_SECONDS) * 100;
  const target = SLIDE_STARTS[index] + SLIDE_META[index].budget;
  const behind = elapsed > target + 3;
  const finished = elapsed >= TOTAL_SECONDS;
  const nextIn = index < SLIDE_META.length - 1 ? SLIDE_STARTS[index + 1] - elapsed : null;

  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="deck-topbar">
      <div className="relative h-[3px] w-full bg-white/[0.07]" data-testid="pitch-progress-bar">
        <div
          className="absolute inset-y-0 left-0 bg-[var(--dash)] shadow-[0_0_14px_var(--dash)]"
          style={{ width: `${pct}%`, transition: "width 120ms linear" }}
          data-testid="pitch-progress-fill"
        />
        {SLIDE_STARTS.slice(1).map((s) => (
          <span
            key={s}
            className="absolute top-0 h-[7px] w-px bg-zinc-500"
            style={{ left: `${(s / TOTAL_SECONDS) * 100}%` }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between gap-4 bg-gradient-to-b from-[#07080b] via-[#07080b]/80 to-transparent px-5 py-3 sm:px-8 lg:px-14">
        <div className="flex items-center gap-3">
          <DashMark className="h-7 w-auto" />
          <div className="leading-tight">
            <p className="font-display text-sm font-semibold tracking-tight text-zinc-50">Dash AI Assistant</p>
            <p className="hidden font-mono-d text-[11px] uppercase tracking-[0.2em] text-zinc-400 sm:block">
              Emergent Building Indonesia
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {rehearse && (
            <span data-testid="rehearsal-indicator" className="hidden items-center gap-2 rounded-full border border-dash/60 bg-dash/10 px-3 py-1 font-mono-d text-[11px] uppercase tracking-[0.18em] text-orange-200 md:inline-flex">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-dash" />
              {nextIn !== null && nextIn > 0 ? `Auto · next ${fmt(nextIn)}` : "Auto · last slide"}
            </span>
          )}
          <button
            onClick={onRehearse}
            aria-label="Toggle rehearsal mode (a)"
            title="Rehearsal mode — auto-advance slides (a)"
            data-testid="rehearsal-toggle-button"
            className={`flex h-9 items-center gap-2 rounded-full border px-3 text-xs transition-colors duration-300 ${
              rehearse ? "border-dash bg-dash text-black" : "border-white/10 bg-white/[0.03] text-zinc-200 hover:border-dash"
            }`}
          >
            <Timer size={15} />
            <span className="hidden lg:inline">{rehearse ? "Stop rehearsal" : "Rehearse"}</span>
          </button>
          <span
            data-testid="pitch-pace-indicator"
            className={`hidden rounded-full border px-3 py-1 font-mono-d text-[11px] uppercase tracking-[0.18em] md:inline-block ${
              finished
                ? "border-zinc-600 text-zinc-300"
                : behind
                ? "border-red-400/50 text-red-300"
                : "border-emerald-400/40 text-emerald-300"
            }`}
          >
            {finished ? "Time" : behind ? "Speed up" : "On pace"}
          </span>
          <span data-testid="pitch-timer-remaining" className="w-14 text-right font-mono-d text-lg tabular-nums text-zinc-50">
            {fmt(TOTAL_SECONDS - elapsed)}
          </span>
          <span className="relative">
            {!running && elapsed === 0 && <span className="pulse-ring pointer-events-none absolute inset-0 rounded-full border border-dash" />}
            <IconBtn onClick={toggle} label={running ? "Pause timer" : "Start presentation"} testId="timer-play-pause-button">
              {running ? <Pause size={15} /> : <Play size={15} />}
            </IconBtn>
          </span>
          <IconBtn onClick={reset} label="Reset timer" testId="timer-reset-button">
            <RotateCcw size={15} />
          </IconBtn>
          <span className="hidden sm:block">
            <IconBtn onClick={openPresenter} label="Open presenter notes (s)" testId="open-presenter-view-button">
              <Presentation size={15} />
            </IconBtn>
          </span>
          <span className="hidden sm:block">
            <IconBtn onClick={fullscreen} label="Fullscreen" testId="fullscreen-toggle-button">
              <Maximize2 size={15} />
            </IconBtn>
          </span>
        </div>
      </div>
    </header>
  );
};
